import type { Social_Media_Posts } from "@/src/gql/graphql";
import type { DetailAction } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { DetailPageConfig } from "@/components/shared/UniversalDetailPage/types/detailPage";
import {
  socialMediaPostDetailTabs,
  socialMediaPostDetailDefaultTab,
} from "@/features/social-media-posts/config/social-media-post-detail-tabs";

/**
 * Statuses from which a user may still publish. "publishing" is in-flight and
 * terminal states (posted/partially_failed/failed/canceled) are done, so
 * "Post now" is hidden for them. Mirrors the lifecycle in post-status.ts /
 * publish-post.ts.
 */
const PUBLISHABLE_STATUSES = new Set(["draft", "scheduled"]);

/**
 * Strongly-typed detail/row actions for social media posts.
 *
 * Status is OWNED BY THE PUBLISH PIPELINE, not free-text: the only way to
 * actually post (fan out to platforms via Zapier) is "Post now", which fires the
 * irreversible double-confirm modal. Changing the status field by hand never
 * published — that path was removed to stop it silently lying. The status column
 * stays a read-only badge (see EnhancedSocialMediaPostsConfig). Action set:
 * View + Post now + Ask Meridian + admin Delete.
 */
export const socialMediaPostDetailActions: DetailAction<Social_Media_Posts>[] = [
  {
    id: "view-details",
    label: "View Details",
    icon: "eye",
    variant: "outline",
    primary: true,
    action: "viewDetails",
  },
  {
    id: "post-now",
    label: "Post now",
    icon: "send",
    variant: "default",
    primary: false,
    action: "publishSocialPost",
    // Only a not-yet-published post can be published on demand.
    visible: (item) => PUBLISHABLE_STATUSES.has(String(item.status ?? "")),
    tooltip: "Publish this post to its platforms now (cannot be undone)",
  },
  {
    id: "ask-meridian",
    label: "Ask Meridian",
    icon: "meridian",
    variant: "outline",
    primary: false,
    action: "askMeridian",
    tooltip: "Ask Meridian AI assistant about this post",
  },
  {
    id: "delete",
    label: "Delete",
    icon: "trash2",
    variant: "destructive",
    primary: false,
    action: "deleteItem",
    allowedRoles: ["wtc_admin"],
    tooltip: "Delete this social media post record (admin only)",
  },
];

/**
 * Detail page configuration for social media posts. Strongly typed (mirrors
 * OfferingDetailConfig) — the Details tab renders from the table config's
 * `fields` map via the generic FieldsTabContent renderer, so field metadata is
 * not re-declared here (single source of truth = EnhancedSocialMediaPostsConfig).
 */
export const SOCIAL_MEDIA_POST_DETAIL_CONFIG: DetailPageConfig<Social_Media_Posts> = {
  entity: "social_media_posts",
  displayName: "Social Post",

  titleTemplate: (item: Social_Media_Posts) => item.title || item.body || "Social Post",

  tabs: socialMediaPostDetailTabs,
  defaultTab: socialMediaPostDetailDefaultTab,
  actions: socialMediaPostDetailActions,
  detailActions: socialMediaPostDetailActions,

  showHeader: true,
  showActions: true,
};
